import { useState } from "react";
import { setProjectSetup } from "../api/projects";
import type { DeployTarget, Detect, Project } from "../api/types";
import { PRO_TIP } from "../lib/edition";
import { DETECT_CHOICES, TARGET_CHOICES, effectiveSetup, isSetUp, type Choice } from "../lib/projectSetup";
import { Dialog } from "../ui/Dialog";

/**
 * Project setup: what the model finds and where it runs. Admins only. Both are fixed into a trained model, so changing
 * them on a project that already has models says so before saving.
 */
export function ProjectSetupDialog({ project, hasModels, has, onClose, onSaved }: {
  project: Project; hasModels: boolean; has: (feature: string) => boolean; onClose: () => void; onSaved: (p: Project) => void;
}) {
  const start = effectiveSetup(project);
  const [detect, setDetect] = useState<Detect>(start.detect);
  // A target the edition no longer allows (Pro lapsed) starts the dialog on the CPU target instead.
  const allowed = (t: DeployTarget) => { const f = TARGET_CHOICES.find((x) => x.value === t)?.feature; return !f || has(f); };
  const [target, setTarget] = useState<DeployTarget>(allowed(start.deploy_target) ? start.deploy_target : "onnxruntime");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const changed = !isSetUp(project) || detect !== start.detect || target !== start.deploy_target;
  const retrain = hasModels && isSetUp(project) && changed;

  const save = async () => {
    setBusy(true); setError("");
    try { onSaved(await setProjectSetup(project.id, { detect, deploy_target: target })); }
    catch (e) { setError((e as Error).message); setBusy(false); }
  };

  const cards = <T extends string>(name: string, choices: Choice<T>[], value: T, set: (v: T) => void) => (
    <div className="target-cards" role="radiogroup" aria-label={name}>
      {choices.map((c) => {
        const open = !c.feature || has(c.feature);
        return (
          <label key={c.value} className="target-card" aria-disabled={!open} title={open ? undefined : PRO_TIP}>
            <input type="radio" name={name} value={c.value} checked={value === c.value} disabled={!open} onChange={() => set(c.value)}
              data-testid={`setup-${name}-${c.value}`} />
            <span className="target-body">
              <span className="target-title">{c.title}{!open && <span className="pill pro">Pro</span>}</span>
              <span className="muted">{c.text}</span>
              {c.note && value === c.value && <span className="warning-text">{c.note}</span>}
            </span>
          </label>
        );
      })}
    </div>
  );

  return (
    <Dialog title="Project setup" onClose={onClose} width={600} testId="project-setup">
      <p className="muted" style={{ marginTop: 0 }}>
        Two choices that shape the model for {project.name}. Training follows them, and only an admin can change them.
      </p>
      <div className="field-caption" style={{ margin: "4px 0 8px" }}>What should the model find?</div>
      {cards("detect", DETECT_CHOICES, detect, setDetect)}
      <div className="field-caption" style={{ margin: "16px 0 8px" }}>Where will it run?</div>
      {cards("target", TARGET_CHOICES, target, setTarget)}
      {retrain && (
        <div className="warning-text" style={{ marginTop: 12 }} data-testid="setup-retrain">
          The models trained so far were built for the old setup. Train again after saving; the old versions stay available.
        </div>
      )}
      {error && <div className="error-text" style={{ marginTop: 8 }} role="alert">{error}</div>}
      <div className="dialog-actions">
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn primary" disabled={busy || !changed} onClick={() => void save()} data-testid="setup-save">{busy ? "Saving…" : "Save setup"}</button>
      </div>
    </Dialog>
  );
}
